
import React from "react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Calendar, 
  Edit, 
  Trash2, 
  MoreHorizontal, 
  BookText,
  Users,
  Clock,
  Tag
} from "lucide-react";
import { SermonPlan } from "@/hooks/use-sermon-plans";
import { format } from "date-fns";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

interface SermonPlanCardProps {
  sermon: SermonPlan;
  onEdit: (sermon: SermonPlan) => void;
  onDelete: (id: string) => void;
}

export function SermonPlanCard({ sermon, onEdit, onDelete }: SermonPlanCardProps) {
  const formattedDate = sermon.planned_date
    ? format(new Date(sermon.planned_date), "MMMM d, yyyy")
    : "No date set";

  return (
    <Card className={`overflow-hidden border ${sermon.auto_generated ? 'border-purple-200 bg-purple-50' : 'border-gray-200'}`}>
      <CardContent className="p-6">
        <div className="flex justify-between items-start">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold">{sermon.title}</h3>
              {sermon.auto_generated && (
                <Badge variant="secondary" className="bg-purple-100 text-purple-800 hover:bg-purple-200">
                  AI Generated
                </Badge>
              )}
            </div>
            
            {sermon.scripture_reference && (
              <div className="flex items-center text-sm text-gray-600">
                <BookText className="mr-1.5 h-3.5 w-3.5" />
                {sermon.scripture_reference}
              </div>
            )}
            
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
              {sermon.planned_date && (
                <div className="flex items-center text-sm text-gray-600">
                  <Calendar className="mr-1.5 h-3.5 w-3.5" />
                  {formattedDate}
                </div>
              )}
              
              {sermon.congregations?.name && (
                <div className="flex items-center text-sm text-gray-600">
                  <Users className="mr-1.5 h-3.5 w-3.5" />
                  {sermon.congregations.name}
                </div>
              )}
              
              {sermon.sermon_type && (
                <div className="flex items-center text-sm text-gray-600">
                  <Tag className="mr-1.5 h-3.5 w-3.5" />
                  {sermon.sermon_type}
                </div>
              )}
              
              {sermon.target_duration && (
                <div className="flex items-center text-sm text-gray-600">
                  <Clock className="mr-1.5 h-3.5 w-3.5" />
                  {sermon.target_duration} minutes
                </div>
              )}
            </div>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">Open menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(sermon)}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-red-600"
                onClick={() => onDelete(sermon.id)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {sermon.theme && (
          <div className="mt-3">
            <div className="text-xs text-gray-500 font-medium">Main Theme</div>
            <div className="text-sm">{sermon.theme}</div>
          </div>
        )}
        
        {sermon.notes && (
          <div className="mt-3">
            <div className="text-xs text-gray-500 font-medium">Notes</div>
            <div className="text-sm whitespace-pre-line mt-1">{sermon.notes}</div>
          </div>
        )}
        
        {sermon.key_points && (
          <div className="mt-3">
            <div className="text-xs text-gray-500 font-medium">Key Points</div>
            <div className="text-sm whitespace-pre-line mt-1">{sermon.key_points}</div>
          </div>
        )}
      </CardContent>
      
      <CardFooter className="bg-gray-50 p-4 flex justify-end border-t">
        <Button
          variant="outline"
          size="sm"
          className="mr-2"
          onClick={() => onDelete(sermon.id)}
        >
          <Trash2 className="mr-2 h-3.5 w-3.5" />
          Delete
        </Button>
        <Button size="sm" onClick={() => onEdit(sermon)}>
          <Edit className="mr-2 h-3.5 w-3.5" />
          Edit
        </Button>
      </CardFooter>
    </Card>
  );
}
